import { useMutation } from "@apollo/client/react";
import {
  DSCardWrapper,
  DSTextarea,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import { faPaw } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNoteFeedbackTrigger } from "@hooks/use-note-feedback-trigger";
import React, { useState } from "react";

import ADD_FEEDBACK_TO_LOG from "../../../graphql/mutations/AddFeedbackToLog";
import { alert } from "../../common/Alert";

export default function PromptFeedbackWidget({
  jobType,
  noteUuid,
  userUuid,
  thumbsUp,
  feedback,
}) {
  const [submittedResult, setSubmittedResult] = useState(
    thumbsUp === true ? "good" : thumbsUp === false ? "bad" : null,
  );
  const [additionalFeedback, setAdditionalFeedback] =
    useState(feedback);
  const [showModal, setShowModal] = useState(false);
  const [addFeedbackToLog] = useMutation(ADD_FEEDBACK_TO_LOG);
  const { triggerNoteFeedbackPrompt } = useNoteFeedbackTrigger();
  const ifBadTemplateFeedback =
    jobType === "template" && submittedResult === "bad";

  function handleSubmit(result) {
    if (result === "good") {
      setSubmittedResult("good");
      alert(
        "success",
        "Glad to hear that! We'll use this feedback to improve future AI-generated results.",
      );
      // not going to hit endpoint for good results until user decides whether to provide additional feedback in modal
      setShowModal(true);
    }
    if (result === "bad") {
      setSubmittedResult("bad");
      alert(
        "warning",
        "Oops, sorry this result wasn't quite good! We'll use this feedback to improve future AI-generated results.",
      );
      // not going to hit endpoint for bad results until user decides whether to provide additional feedback in modal
      setShowModal(true);
    }
  }

  const handleFeedbackSubmission = (
    includeAdditionalFeedback = false,
  ) => {
    // feedback data payload
    const feedbackData = {
      variables: {
        userUuid: userUuid,
        noteUuid: noteUuid,
        rating: submittedResult,
        noteType: jobType,
      },
    };

    // If additional feedback should be included, add it to the feedbackData
    if (includeAdditionalFeedback) {
      feedbackData.variables.feedback = additionalFeedback;
    }

    // Submit feedback mutation
    addFeedbackToLog(feedbackData);

    // Close modal
    setShowModal(false);

    if (submittedResult === "bad") {
      triggerNoteFeedbackPrompt("thumbs_down", noteUuid);
    }
  };
  return (
    <DSCardWrapper variant="info" dataTestId="prompt-feedback-card">
      <div className="flex flex-row justify-between items-center gap-sm">
        <h5 className="font-medium text-brand">
          What did you think of this AI-generated note?
        </h5>
        <div className="gap-sm flex">
          <button
            onClick={() => handleSubmit("good")}
            className={`${
              submittedResult === "good"
                ? "bg-green-500 text-white"
                : "  hover:text-white bg-secondary dark:hover:bg-green-400 hover:bg-green-400"
            }  rounded-full h-8 shadow-xs transition-colors px-sm`}
          >
            <FontAwesomeIcon icon={faPaw} /> Great!
          </button>
          <button
            onClick={() => handleSubmit("bad")}
            className={`${
              submittedResult === "bad"
                ? "bg-red-500 text-white"
                : "  hover:text-white bg-secondary dark:hover:bg-red-400 hover:bg-red-400"
            }  rounded-full h-8 shadow-xs transition-colors px-sm`}
          >
            <FontAwesomeIcon
              icon={faPaw}
              className="transform -rotate-180"
            />{" "}
            Could be better.
          </button>
        </div>
      </div>
      <DSModal
        isOpen={showModal}
        onClose={() => handleFeedbackSubmission(false)}
        title="Additional Feedback"
        subtitle={
          <span>
            Please note that this feedback is not currently used to
            automatically improve records.{" "}
            {ifBadTemplateFeedback && (
              <span>
                Attached is a guide to help you improve your template:
                &nbsp;
                <a
                  href="https://scribenote-assets.s3.us-east-2.amazonaws.com/template_tips.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline"
                >
                  Free Template Guide
                </a>
              </span>
            )}
          </span>
        }
        actions={[
          {
            label: "Skip",
            variant: "cancel",
            onClick: () => handleFeedbackSubmission(false),
          },
          {
            label: "Submit",
            variant: "primary",
            onClick: () => handleFeedbackSubmission(true),
            dataTestId: "confirm-button",
          },
        ]}
        dataTestId="prompt-feedback-modal"
      >
        <div className="my-md">
          <DSTextarea
            minRows={3}
            placeholder="Let us know how we can improve this note (optional)"
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
            dataTestId="prompt-feedback-textarea"
          />
        </div>
      </DSModal>
    </DSCardWrapper>
  );
}
